/* eslint-disable no-var,prefer-const */

import $axios from '../../plugins/axios';
import $moment from '../../plugins/moment';

import { abortableRequest, createDict, createIndex, Dict, divideTimeRange, fetchAllPages, ignoringAbort } from '~/helpers';
import { Admin, AdminsResponse, Api, Area, Availability, AvailabilityResponse, DeleteEventGroupParams, Event, EventRequest, EventType, Excuse, Filter, GetAdminParams, GetAreaParams, GetEventTypeParams, GetEventsParams, GetExcusesParams, GetFilterParams, GetPlannerHolidayParams, GetPosFilterPosInfoParams, GetPosParams, GetRegionParams, GetRoleParams, GetSettingPosTypeParams, GetTerritoryParams, GroupEventRequest, GroupEventRequestEntity, GroupEventsRequest, PaginatorResult, PlannerHoliday, Pos, PosInfo, PosInfoCount, PosLight, PosType, PosesResponse, Region, Role, RoleLight, SingleEventRequest, SingleEventRequestEntity, Territory } from '~/api';
import { createFiltersModule, defineSingleFilter, defineArrayFilter, buildModule } from '../utils';
import moment from 'moment';

const api = $axios as Api;

export type PosCategoryFilter = Filter & { infoType: 'pos'; posInfo: PosInfo };

export type ConflictDate = {
	date: string;
	admins: (Availability & { name: string })[];
};

export type ActivityFormMode = 'group' | 'single';
export type ExtendedEvent = Event & {
	groupEvents: Event[] | null;
};

const ACTIVITY_DEFAULT = {
	id: null,
	start: '',
	end: '',
	startTime: '07:00',
	endTime: '07:00',
	status: 'booked',
	note: null,
	freezeNote: null,
	admins: [],
	supervisors: [],
	eventType: null,
	role: null,
	groupId: null,
	posIds: [],
	mode: 'single',
	request: null,
};

const COMMON_FILTERS = {
	searchTerm: defineSingleFilter('search', String),
	areaId: defineSingleFilter('area', Number),
	regionId: defineSingleFilter('region', Number),
	territoryId: defineSingleFilter('territory', Number),
	plannedPeriod: defineSingleFilter('plannedPeriod', String),
	customFilterIds: defineArrayFilter('filterId', Number),
	customFilterValues: defineArrayFilter('filterValue', String),
	eventType: defineArrayFilter('eventType', Number),
	plannedFrom: defineSingleFilter('plannedFrom', String),
	plannedTo: defineSingleFilter('plannedTo', String),
	note: defineSingleFilter('note', String),
	hasIncompleteEvent: defineSingleFilter('hasIncompleteEvent', Boolean),
	groupId: defineSingleFilter('groupId', String),
	hasSupervisors: defineSingleFilter('hasSupervisors', Boolean),
};

export default buildModule()
	.withSubmodules({
		posFilters: createFiltersModule({
			filters: {
				...COMMON_FILTERS,
				posTypeId: defineArrayFilter('posType', Number),
				eventCapacity: defineSingleFilter('eventCapacity', Number),
			},
			replaceRoute: true,
		}),
		adminFilters: createFiltersModule({
			filters: {
				...COMMON_FILTERS,
				roleId: defineArrayFilter('role', Number),
			},
			replaceRoute: true,
		}),
	})
	.withState({
		plannablePoses: null as PosesResponse | null,
		plannableAdmins: null as AdminsResponse | null,

		poses: [] as Pos[],
		posesTotalCount: null as number | null,
		storedPoses: {} as Dict<Pos>,
		posTypes: [] as PosType[],
		posTypesSelected: [] as PosType[],
		users: [] as Admin[],
		storedUsers: {} as Dict<Admin>,
		supervisors: [] as Admin[],
		storedSupervisors: {} as Dict<Admin>,
		roles: [] as RoleLight[],
		dates: [] as string[],
		datesClone: [] as string[],
		activitiesAll: [] as ExtendedEvent[],
		activityTypes: [] as EventType[],

		// TODO consider refactoring these (partially) to local state, otherwise type here properly
		activityForm: null as any, // eslint-disable-line @typescript-eslint/no-explicit-any
		activityFormClone: null as any, // eslint-disable-line @typescript-eslint/no-explicit-any

		activityPos: null,
		activityAdmins: [],
		activityRole: null,
		activityEventType: null,

		checkingAvailability: false,
		conflictDates: [] as ConflictDate[],
		isEventConflict: false,

		holidays: [] as PlannerHoliday[],
		excuses: [] as Excuse[],

		typesByRole: [],
		usersByRole: [],

		eventsLoading: false,

		areas: [] as Area[],
		regions: [] as Region[],
		territory: [] as Territory[],
		regionPaginator: null as PaginatorResult | null,
		areaPaginator: null as PaginatorResult | null,
		territoryPaginator: null as PaginatorResult | null,

		customFilters: [] as Filter[],
		customFiltersLoaded: false,

		posCategoryFilter: null as PosCategoryFilter | null,
		posCategoryFilterLoaded: false,
		posCategoryCounts: [] as PosInfoCount[],

		presentGroupIdsWithDay: [] as string[],
	})
	.withGetters({
		plannablePoses: state => state.plannablePoses?.payload ?? [],
		plannablePosesTotalCount: state => state.plannablePoses?.paginator.totalCount ?? 0,
		plannableAdmins: state => state.plannableAdmins?.payload ?? [],
		plannableAdminsTotalCount: state => state.plannableAdmins?.paginator.totalCount ?? 0,

		conflictDates: state => state.conflictDates,
		adminConflictDatesById: state => (adminId: number) => {
			return state.conflictDates.filter(date => {
				return date.admins.some(admin => admin.adminId === adminId);
			}).map(date => date.date);
		},
		userEvents: state => (userId: number) => {
			return state.activitiesAll.filter(activity => activity.admins.some(admin => admin.id === userId));
		},
		presentGroupIdsWithDay: state => state.presentGroupIdsWithDay,
	})
	.withGetters({
		conflictingEvents: (_, getters) => (adminId: number, start: string, end: string) => {
			const plannedRange = $moment.range($moment(start), $moment(end));
			return getters.userEvents(adminId).filter(event => {
				const eventRange = $moment.range($moment(event.start), $moment(event.end));
				return eventRange.overlaps(plannedRange);
			});
		},
		poses: state => state.poses,
		posesTotalCount: state => state.posesTotalCount,
		storedPoses: state => state.storedPoses,
		posById: state => (posId: number) => (
			state.storedPoses[posId] ?? null
		),
		posesAvailable: (state, getters) => {
			return state.poses.map(pos => {
				// @ts-expect-error TODO refactor this
				pos.$isDisabled = getters.posDisabled(pos);
				return pos;
			});
		},
		posDisabled: (state, getters) => (pos: Pos) => {
			// @ts-expect-error TODO reorder / refactor
			const selectedHolidays: PlannerHoliday[] = getters.selectedHolidays;
			const selectedRole = state.activityForm.role;

			return selectedHolidays.some(holiday => {
				// holiday pro vsechny posy
				const forAllPoses = holiday.poses.length === 0;
				// holiday pro nektere posy (a ty mame v nabidce)
				const forVisiblePos = holiday.poses.some(holidayPos => holidayPos.id === pos.id);

				// holiday pro vsechny role
				const forAllRoles = holiday.roles.length === 0;
				// holiday ne pro vsechny role, ale pro zrovna vybranou ano (a nejakou vybranou mame)
				const forSelectedRole = !forAllRoles && (!holiday.roles.some(role => role.id === selectedRole) && selectedRole);

				return (forAllPoses || forVisiblePos) && (forAllRoles || forSelectedRole);
			});
		},
		users: state => state.users,
		storedUsers: state => state.storedUsers,
		userById: state => (userId: number): Admin | null => (
			state.storedUsers[userId] ?? null
		),

		supervisors: state => state.supervisors,
		supervisorById: state => (id: number): Admin | null => (
			state.storedSupervisors[id] ?? null
		),

		regionTotalCount: state => state.regionPaginator?.totalCount ?? 0,

		posTypes: state => state.posTypes,
		posTypesSelected: state => state.posTypesSelected,
		roles: state => state.roles,
	})
	.withGetters({
		rolesDisabled: (state, getters) => {
			// @ts-expect-error TODO reorder / refactor
			const selectedHolidays: PlannerHoliday[] = getters.selectedHolidays;
			const selectedPosIds = state.activityForm.posIds || [];
			const disabledRoles = getters.roles.filter(role => {
				return selectedHolidays.some(holiday => {

					if (holiday.poses.length && selectedPosIds.length === 0) {
						return false;
					}

					if (holiday.poses.length && selectedPosIds.length > 0 && !holiday.poses.some(pos => selectedPosIds.includes(pos.id))) {
						return false;
					}

					return !holiday.roles.some(holidayRole => holidayRole.id === role.id);
				});
			});
			return disabledRoles;
		},
		dates: state => state.dates,
		datesClone: state => state.datesClone,
	})
	.withGetters({
		activitiesAll: state => state.activitiesAll,
		activitiesLoading: state => state.eventsLoading,
		activityForm: state => state.activityForm,
		activityFormClone: state => state.activityFormClone,
		selectedUserNameById: state => (id: number) => {
			// @ts-expect-error TODO consider refactoring to local state, otherwise type here properly
			const user = state.activityForm?.admins.find(admin => admin.id === id);
			if (!user) {
				return;
			}
			return user.personalData.fullname;
		},

		resourceItemsIdsByExcludedRoles: state => (roleIds: number[]) => {
			// @ts-expect-error TODO refactor this after splitting the joint planning view
			return state.resourceItems.filter(resource => !roleIds.includes(resource.role?.id)).map(resource => resource.id);
		},
	})
	.withGetters({
		holidays: state => state.holidays.map(holiday => {
			return holiday;
		}),

		selectedHolidays: (state, getters) => {
			// TODO: pridat casy start-end
			const selected = getters.dates;
			return state.holidays.filter(holiday => {
				return selected.some(selectedDate => {
					return $moment(selectedDate).isBetween(holiday.start, holiday.end, 'minute', '[]');
				});
			});
		},

		filteredHolidays: state => ({ roleId = '', posIds = [] }: {
			roleId?: string;
			posIds?: number[];
		}) => {
			const hasPosFilter = posIds.length > 0;
			const hasRoleFilter = roleId !== '' || parseInt(roleId) === 0;

			return state.holidays.filter(holiday => {
				const forAllPoses = holiday.poses.length === 0;
				const forAllRoles = holiday.roles.length === 0;

				// globalni nepracovni den, plati pro vsechny vsude
				if (forAllPoses && forAllRoles) {
					return true;
				}

				// pokud je pro vsechny posy, nezalezi na vyberu, jinak hledame, jestli holiday plati pro nektery z vybranych posu
				const forThisPos = forAllPoses || (hasPosFilter && holiday.poses.some(pos => posIds.includes(pos.id)));
				if (!forThisPos) {
					return false;
				}

				// pokud plati pro tento (nebo vsechny) posy, pak kontrolujeme, jestli je bud bez vyjimek pro pos, nebo nema vybrana role vyjimku
				const forThisRole = forAllRoles || (!holiday.roles.some(role => role.id === parseInt(roleId)) && hasRoleFilter);
				return forThisRole;
			});
		},

		excuses: state => state.excuses,

		usersByRole: state => state.usersByRole,
		typesByRole: state => state.typesByRole,

		activityTypes: state => state.activityTypes,

		areas: state => state.areas,
		regions: state => state.regions,
		territory: state => state.territory,

		posCustomFilters: state => state.customFilters.filter(f => f.infoType === 'pos'),
		adminCustomFilters: state => state.customFilters.filter(f => f.infoType === 'admin'),

		posCategoryFilter: state => state.posCategoryFilter,
		posCategoryCounts: state => state.posCategoryCounts,
	})
	.withMutations({
		EVENTS_LOADING (state, isLoading: boolean) {
			state.eventsLoading = isLoading;
		},

		ACTIVITIES_SUCCESS (state, activities: ExtendedEvent[]) {
			state.activitiesAll = activities;
		},

		HOLIDAYS_SUCCESS (state, holidays: PlannerHoliday[]) {
			state.holidays = holidays;
		},

		EXCUSES_SUCCESS (state, excuses: Excuse[]) {
			state.excuses = excuses;
		},

		PLANNABLE_POSES_SUCCESS (state, response: PosesResponse) {
			state.plannablePoses = response;
		},

		PLANNABLE_ADMINS_SUCCESS (state, response: AdminsResponse) {
			state.plannableAdmins = response;
		},

		REGIONS_PAGINATION (state, paginator: PaginatorResult) {
			state.regionPaginator = paginator;
		},

		POSES_SUCCESS (state, poses: Pos[]) {
			state.poses = poses;
		},

		POSES_TOTAL_COUNT_SUCCESS (state, totalCount: number | null) {
			state.posesTotalCount = totalCount;
		},

		SET_STORED_POSES (state, poses: Pos[]) {
			state.storedPoses = {
				...state.storedPoses,
				...createDict(poses, (pos) => pos.id),
			};
		},

		POS_TYPES_SUCCESS (state, posTypes: PosType[]) {
			state.posTypes = posTypes;
		},

		POS_TYPES_SELECTED (state, posTypes: PosType[]) {
			state.posTypesSelected = posTypes;
		},

		USERS_SUCCESS (state, users: Admin[]) {
			state.users = users;
		},

		SET_STORED_USERS (state, admins: Admin[]) {
			state.storedUsers = {
				...state.storedUsers,
				...createDict(admins, (admin) => admin.id),
			};
		},

		SET_SUPERVISORS (state, supervisors: Admin[]) {
			state.supervisors = supervisors;
		},

		SET_STORED_SUPERVISORS (state, supervisors: Admin[]) {
			state.storedSupervisors = {
				...state.storedSupervisors,
				...createDict(supervisors, ({ id }) => id),
			};
		},

		ROLES_SUCCESS (state, roles: RoleLight[]) {
			state.roles = roles;
		},

		TYPES_SUCCESS (state, types: EventType[]) {
			state.activityTypes = types;
		},

		ADD_ACTIVITY: (state, activity: ExtendedEvent) => {
			state.activitiesAll.push(activity);
		},

		ADD_ACTIVITY_NEW: (state, { start, end, pos, admins, startTime, endTime }: {
			start: string;
			end: string;
			pos: Pos | null;
			admins: Admin[];
			startTime: string;
			endTime: string;
		}) => {
			const updatedForm = {
				...ACTIVITY_DEFAULT,
				start,
				end,
				posIds: pos ? [ pos.id ] : [],
				admins,
				startTime,
				endTime,
			};

			state.activityEventType = null;
			state.activityForm = { ...updatedForm };
			state.activityFormClone = { ...updatedForm };
		},

		SET_PRESENT_GROUP_IDS (state, groupIds: string[]) {
			state.presentGroupIdsWithDay = groupIds;
		},

		DELETE_ACTIVITY_NEW: (state) => {
			state.usersByRole = [];
			state.typesByRole = [];
			// state.dates = [];
			state.activityForm = null;
		},

		SET_ACTIVITY_ROLE: (state, { roleId, isUsers }: {
			roleId: number;
			isUsers: boolean;
		}) => {
			if (state.activityForm) { state.activityForm.role = roleId; }

			// Clear admins if no longer fit the role
			if (!isUsers && (state.activityForm && state.activityForm.admins.length)) {
				// @ts-expect-error TODO refactor this to planning popup's local state mgmt
				state.activityForm.admins = state.activityForm.admins.filter(admin => {
					// @ts-expect-error TODO refactor this to planning popup's local state mgmt
					return state.usersByRole.map(usr => usr.id).includes(admin.id);
				});
			}
		},

		SET_ACTIVITY_TYPE (state, type: number) {
			state.activityForm.eventType = type;
		},

		SET_ACTIVITY_POS_IDS (state, posIds: number[]) {
			state.activityForm.posIds = posIds;
		},

		SET_ACTIVITY_GROUP_ID (state, groupId: string | null) {
			state.activityForm.groupId = groupId;
		},

		SET_ACTIVITY_MODE (state, mode: ActivityFormMode) {
			state.activityForm.mode = mode;
		},

		SET_ACTIVITY_START_TIME (state, value: string) {
			state.activityForm.startTime = value;
		},

		SET_ACTIVITY_END_TIME (state, value: string) {
			state.activityForm.endTime = value;
		},

		SET_ACTIVITY_USER (state, user: Admin[]) {
			state.activityForm.admins = user;
		},

		SET_ACTIVITY_SUPERVISORS (state, supervisors: number[]) {
			state.activityForm.supervisors = supervisors;
		},

		SET_ACTIVITY_DATES (state, dates: string | string[]) {
			state.dates = typeof dates === 'string' ? dates.split(', ') : dates;
		},

		SET_ACTIVITY_DATES_CLONE (state, dates: string | string[]) {
			state.datesClone = typeof dates === 'string' ? dates.split(', ') : dates;
		},

		SET_ACTIVITY_NOTE (state, note: string | null) {
			state.activityForm.note = note;
		},

		SET_ACTIVITY_FREEZE_NOTE (state, freezeNote: string | null) {
			state.activityForm.freezeNote = freezeNote || null;
		},

		AREAS_SUCCESS (state, areas: Area[]) {
			state.areas = areas;
		},

		AREA_PAGINATOR (state, paginator: PaginatorResult) {
			state.areaPaginator = paginator;
		},

		ADD_AREAS (state, areas: Area[]) {
			state.areas = state.areas.concat(areas);
		},

		REGIONS_SUCCESS (state, regions: Region[]) {
			state.regions = regions;
		},

		TERRITORY_SUCCESS (state, territory: Territory[]) {
			state.territory = territory;
		},

		TERRITORY_PAGINATOR (state, paginator: PaginatorResult) {
			state.territoryPaginator = paginator;
		},

		ADD_TERRITORIES (state, territory: Territory[]) {
			state.territory = state.territory.concat(territory);
		},

		CONFLICT_DATES (state, availability: ConflictDate[]) {
			state.conflictDates = availability;
		},

		IS_EVENT_CONFLICT (state, availability: boolean) {
			state.isEventConflict = availability;
		},

		CHECKING_AVAILABILITY (state, value: boolean) {
			state.checkingAvailability = value;
		},

		CUSTOM_FILTERS_SUCCESS (state, value: Filter[]) {
			state.customFilters = value;
			state.customFiltersLoaded = true;
		},

		POS_CATEGORY_FILTER_SUCCESS (state, filter: PosCategoryFilter | null) {
			state.posCategoryFilter = filter;
			state.posCategoryFilterLoaded = true;
		},

		POS_CATEGORY_COUNTS_SUCCESS (state, counts: PosInfoCount[]) {
			state.posCategoryCounts = counts;
		},
	})
	.withActions({
		clearActivity ({ state, commit }) {
			state.dates = [];

			let daysBetween = $moment(state.activityFormClone.end).diff($moment(state.activityFormClone.start), 'days');
			for (var i = 0; i <= daysBetween; i++) {
				let date = $moment(state.activityFormClone.start).add(i, 'days').format('YYYY-MM-DD');
				state.dates.push(date);
			}

			state.activityForm = { ...state.activityFormClone };

			if (!state.activityFormClone.id) {
				commit('SET_ACTIVITY_ROLE', {
					roleId: state.activityForm.admins[0]?.role.id || state.activityForm.admins[0]?.role,
					isUsers: true,
				});
			}
		},

		clearActivityNew ({ state, commit }) {
			state.dates = [];
			let start = state.activityForm.start;
			let end = state.activityForm.end;

			let daysBetween = $moment(state.activityForm.end).diff($moment(state.activityForm.start), 'days');
			for (var i = 0; i <= daysBetween; i++) {
				let date = $moment(state.activityForm.start).add(i, 'days').format('YYYY-MM-DD');
				state.dates.push(date);
			}

			state.activityForm = Object.assign({}, ACTIVITY_DEFAULT);

			state.activityForm.eventType = state.activityEventType;
			state.activityForm.admins = state.activityAdmins;
			commit('SET_ACTIVITY_ROLE', {
				roleId: state.activityForm.admins[0]?.role.id || state.activityForm.admins[0]?.role,
				isUsers: true,
			});

			// state.activityForm.pos = state.activityPos;

			state.activityForm.start = start;
			state.activityForm.end = end;
		},

		async getActivitiesAll ({ commit }, params: GetEventsParams & { resource?: 'admins' | 'poses' } = { resource: 'admins' }) {
			commit('EVENTS_LOADING', true);

			const events = await fetchAllPages('/events', params)<Event>(api);

			const eventsWithGroup = filterEventsWithGroup(events);
			const eventsWithoutGroup = events.filter(({ groupId }) => groupId === null);

			const groupIdentifiers = createDict(eventsWithGroup.map(({ start, groupId }) => ({ start, groupId })), (info) => {
				const day = moment(info.start).format('YYYY-MM-DD');
				return `${ info.groupId }-${ day }`;
			});

			const groupEventsFillResponses = await Promise.all(Object.values(groupIdentifiers).map(({ start, groupId }) => api.get('/events', { params: {
				start: moment(start).format('YYYY-MM-DD'),
				groupId,
			} })));

			const mergedGroupEvents = createDict([
				...groupEventsFillResponses.flatMap(({ data }) => data.payload),
				...eventsWithGroup,
			], ({ id }) => id);

			const eventsGroupedByIdAndDay = createIndex(Object.values(mergedGroupEvents), ({ groupId, start }) => {
				const day = moment(start).format('YYYY-MM-DD');
				return `${ groupId }-${ day }`;
			});

			commit('SET_PRESENT_GROUP_IDS', Object.keys(eventsGroupedByIdAndDay));

			const groupedEvents: ExtendedEvent[] = params.resource === 'admins'
				? buildAdminGroups(eventsGroupedByIdAndDay)
				: buildPosGroups(eventsWithGroup, eventsGroupedByIdAndDay);

			const extendedEvents: ExtendedEvent[] = [
				...eventsWithoutGroup.map(event => ({ ...event, groupEvents: null })),
				...groupedEvents,
			];

			commit('ACTIVITIES_SUCCESS', extendedEvents);
			commit('EVENTS_LOADING', false);
		},

		async getHolidays ({ commit }, params: GetPlannerHolidayParams = {}) {
			const holidays = await fetchAllPages('/planner-holiday', params)<PlannerHoliday>(api);
			commit('HOLIDAYS_SUCCESS', holidays);
		},

		async getExcuses ({ commit }, params: GetExcusesParams = {}) {
			const extendedParams: GetExcusesParams = {
				...params,
				status: [ 'accepted', 'waiting' ],
			};

			const excuses = await fetchAllPages('/excuses', extendedParams)<Excuse>(api);
			commit('EXCUSES_SUCCESS', excuses);
		},

		async getPlannablePoses ({ commit }, params: GetPosParams = {}) {
			await ignoringAbort(async () => {
				const { data } = await abortableRequest('getPlannablePoses', (signal) => api.get('/pos', { params, signal }));
				commit('PLANNABLE_POSES_SUCCESS', data);
			});
		},

		async getPlannableAdmins ({ commit }, params: GetAdminParams = {}) {
			await ignoringAbort(async () => {
				const { data } = await abortableRequest('getPlannableAdmins', (signal) => api.get('/admin', { params, signal }));
				commit('PLANNABLE_ADMINS_SUCCESS', data);
			});
		},

		async getPoses ({ commit }, params: GetPosParams = {}) {
			const allowedAdminId = params.allowedAdminId;

			await ignoringAbort(async () => {
				if (allowedAdminId?.length === undefined || allowedAdminId.length < 2) {
					const { data } = await abortableRequest('getPoses', (signal) => api.get('/pos', { params, signal }));
					commit('POSES_SUCCESS', data.payload);
					commit('POSES_TOTAL_COUNT_SUCCESS', data.paginator.totalCount);
					commit('SET_STORED_POSES', data.payload);
					return;
				}

				const allResponses = await abortableRequest('getPoses', (signal) => {
					const posPromises = allowedAdminId.map(adminId => api.get('/pos', { params: { ...params, allowedAdminId: [ adminId ] }, signal }));

					return Promise.all(posPromises);
				});

				const allPosesArrays = allResponses.map(res => res.data.payload);

				const dicts = allPosesArrays.map(poses => createDict(poses, p => p.id));

				const commonIds = Object.keys(dicts[0]).filter(id =>
					dicts.every(dict => id in dict));

				const finalPoses = commonIds.map(id => dicts[0][id]);

				commit('POSES_SUCCESS', finalPoses);
				commit('POSES_TOTAL_COUNT_SUCCESS', null);
				commit('SET_STORED_POSES', finalPoses);
			});
		},

		async getUsers ({ commit }, params: GetAdminParams = {}) {
			const { data } = await api.get('/admin', { params: { ...params, withMe: params.withMe ?? true } });
			commit('USERS_SUCCESS', data.payload);
			commit('SET_STORED_USERS', data.payload);
		},

		async getSupervisors ({ commit }, params: GetAdminParams = {}) {
			const { data } = await api.get('/admin', { params: { ...params, withMe: params.withMe ?? true } });
			commit('SET_SUPERVISORS', data.payload);
			commit('SET_STORED_SUPERVISORS', data.payload);
		},

		async getIntersectedUsersByPosId ({ commit }, params: Omit<GetAdminParams, 'allowedPosId'> & { allowedPosIds: number[] } = { allowedPosIds: [] }) {
			if (params.allowedPosIds.length < 2) {
				const allowedPosId = params.allowedPosIds.length === 1 ? params.allowedPosIds[0] : null;
				const { data } = await api.get('/admin', { params: { ...params, withMe: params.withMe ?? true, allowedPosId } });
				commit('USERS_SUCCESS', data.payload);
				commit('SET_STORED_USERS', data.payload);
			}

			const userPromises = params.allowedPosIds.map(posId => api.get('/admin', { params: { ...params, withMe: params.withMe ?? true, allowedPosId: posId } }));
			const allResponses = await Promise.all(userPromises);

			const allUserArrays = allResponses.map(res => res.data.payload);

			const dicts = allUserArrays.map(users => createDict(users, u => u.id));

			if (dicts.length === 0) {
				return;
			}

			const commonIds = Object.keys(dicts[0]).filter(id =>
				dicts.every(dict => id in dict));

			const finalUsers = commonIds.map(id => dicts[0][id]);

			commit('USERS_SUCCESS', finalUsers);
			commit('SET_STORED_USERS', finalUsers);
		},

		checkUsersPlanningAvailability ({ commit, getters }, { eventId, excuseId, userIds, dates, isAllDay = false, startTime, endTime, posId }: {
			eventId: number;
			excuseId: number;
			userIds: number[];
			dates: string[];
			isAllDay?: boolean;
			startTime: string;
			endTime: string;
			posId: number;
		}) {
			if (!isAllDay && $moment(startTime, 'HH:mm').isSameOrAfter($moment(endTime, 'HH:mm'))) {
				// zbytecne nevalidovat, kdyz startTime >= endTime
				commit('CONFLICT_DATES', []);
				return;
			}
			if (userIds.length === 0) {
				// zbytecne nevalidovat, kdyz nemame uzivatele
				commit('CONFLICT_DATES', []);
				return;
			}
			commit('CHECKING_AVAILABILITY', true);
			const startEndList = dates.map(date => {
				let endDate = date;
				if (isAllDay) {
					endDate = $moment(date).add(1, 'd').format('YYYY-MM-DD');
					startTime = '00:00';
					endTime = '00:00';
				}

				return {
					start: $moment(`${ date }T${ startTime }`).format(),
					end: $moment(`${ endDate }T${ endTime }`).format(),
					admin: userIds,
					event: eventId,
					excuseRequest: excuseId,
					posId: [ posId ],
				};
			});

			const promisesList = startEndList.map(startEnd => (
				api.get('/availability', { params: startEnd })
			));

			return Promise.all(promisesList).then(responses => {
				const allConflictDates = responses.map((response, index) => ({
					date: dates[index],
					admins: response.data.payload
						.filter(availability => !availability.available)
						.map(availability => ({ ...availability, name: getters.selectedUserNameById(availability.adminId) })),
				}));

				commit('CONFLICT_DATES', allConflictDates.filter(date => date.admins.length));
			}).finally(() => {
				commit('CHECKING_AVAILABILITY', false);
			});
		},

		async checkUsersGroupAvailability ({ state, commit, getters }, { eventId, userIds, posIds, dates, startTime, endTime }: {
			eventId: number;
			userIds: number[];
			posIds: number[];
			dates: string[];
			startTime: string;
			endTime: string;
		}) {
			if (moment(startTime, 'HH:mm').isSameOrAfter(moment(endTime, 'HH:mm'))) {
				commit('CONFLICT_DATES', []);
				return;
			}

			if (userIds.length === 0) {
				commit('CONFLICT_DATES', []);
				return;
			}

			const extendedEvent = state.activitiesAll.find(event => event.id === eventId);
			const groupEventWrapperId = extendedEvent?.groupEventWrapperId ?? null;

			commit('CHECKING_AVAILABILITY', true);

			const groupRanges = divideTimeRange(startTime, endTime, posIds.length);

			const availabilityChecks = dates.flatMap(date => groupRanges.map(range => ({
				date,
				request: api.get('/availability', {
					params: {
						start: moment(`${ date }T${ range.start }`).format(),
						end: moment(`${ date }T${ range.end }`).format(),
						admin: userIds,
						posId: posIds,
						groupEventWrapper: groupEventWrapperId,
					},
				}),
			})));

			const availabilityResponses = await Promise.all(availabilityChecks.map(check => check.request));

			const allConflictDates: ConflictDate[] = availabilityChecks
				.map((check, index) => ({
					date: check.date,
					admins: availabilityResponses[index].data.payload
						.filter(response => !response.available)
						.map(response => ({ ...response, name: getters.selectedUserNameById(response.adminId) })),
				}))
				.filter(conflict => conflict.admins.length);

			const conflictsByDate = createIndex(allConflictDates, conflict => conflict.date);

			const mergedConflictDates = Object.entries(conflictsByDate).map(([ date, conflicts ]) => ({
				date,
				admins: [
					...new Map(conflicts
						.flatMap(conflict => conflict.admins)
						.map(availability => [ availability.adminId, availability ])).values(),
				],
			}));

			commit('CONFLICT_DATES', mergedConflictDates);
			commit('CHECKING_AVAILABILITY', false);
		},

		checkUsersAvailabilitySingle ({ commit }, { eventId, excuseId, userId, start, end, posId }: {
			eventId: number;
			excuseId: number;
			userId: number;
			start: string;
			end: string;
			posId: number;
		}) {
			if ($moment(start).isSameOrAfter(end)) {
				// zbytecne nevalidovat, kdyz startTime >= endTime
				commit('IS_EVENT_CONFLICT', false);
				return;
			}
			if (userId === null) {
				// zbytecne nevalidovat, kdyz nemame uzivatele
				commit('IS_EVENT_CONFLICT', false);
				return;
			}

			api.get('/availability', { params: {
				start: start,
				end: end,
				admin: [ userId ],
				event: eventId,
				excuseRequest: excuseId,
				posId: [ posId ],
			} })
				.then(response => {
					commit('IS_EVENT_CONFLICT', response.data.payload.some(availability => availability.adminId === userId && !availability.available));
				});
		},

		async getAdminsAvailability (_, params: {
			eventId: number | null;
			adminIds: number[];
			start: string;
			end: string;
			posId: number[] | null;
			groupId?: string | null;
		}): Promise<AvailabilityResponse | null> {
			if ($moment(params.start).isSameOrAfter(params.end)) { return null; }
			if (params.adminIds.length === 0) { return null; }

			const { data } = await api.get('/availability', { params });

			return data;
		},


		getActivityTypes ({ commit }, params: GetEventTypeParams) {
			return api.get('/event-type', { params: params })
				.then(resp => {
					const types = resp.data.payload;
					commit('TYPES_SUCCESS', types);
				})
				.catch(err => { console.log(err); });
		},

		async getRoles ({ commit }, customParams: GetRoleParams = {}) {
			const params: GetRoleParams = { ...customParams, withMe: true, isInPlanner: true };

			const roles = await fetchAllPages('/role', params)<Role>(api);

			commit('ROLES_SUCCESS', roles);
		},

		async getPosTypes ({ commit }, params: GetSettingPosTypeParams = {}) {
			params = { orderBy: 'name', orderAsc: true, ...params };
			const posTypes = await fetchAllPages('/setting/pos-type', params)<PosType>(api);

			commit('POS_TYPES_SUCCESS', posTypes);
		},

		async getPosTypesSelected ({ commit }, params: GetSettingPosTypeParams = { id: [] }) {
			params = { orderBy: 'name', orderAsc: true, ...params };
			const { data } = await api.get('/setting/pos-type', { params: params });

			commit('POS_TYPES_SELECTED', data.payload);
		},
	})
	.withActions({
		addActivity (_, activity: EventRequest) {
			return api.post('/events', activity)
				.then(resp => resp)
				.catch((err) => err);
		},

		async editActivity (_, { id, ...activity }: { id: number } & EventRequest) {
			await api.put(`/events/${ id }`, activity);
		},

		async deleteActivity (_, activityId: number) {
			await api.delete(`/events/${ activityId }`);
			// commit('DELETE_ACTIVITY', activityId);
		},

		async addGroupActivity (_, request: GroupEventRequest) {
			await api.post('/event-group', request);
		},

		async deleteGroupActivity (_, params: DeleteEventGroupParams) {
			await api.delete('/event-group', { params });
		},

		async updateGroupActivity (_, request: GroupEventRequest) {
			await api.put('/event-group', request);
		},

		async createEventRequest (_, request: SingleEventRequestEntity | GroupEventRequestEntity) {
			return await api.post('/event-request', request);
		},

		async updateEventRequest (_, { id, ...request }: (SingleEventRequestEntity | GroupEventRequestEntity) & { id: SingleEventRequest['id'] | GroupEventsRequest['id'] }) {
			return await api.put(`/event-request/${ id }`, request);
		},

		setPopup ({ state, commit, dispatch }, name: 'users' | 'poses') {
			const startFilter = moment(state.activityForm.start).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
			const endFilter = moment(state.activityForm.end).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');

			Promise.all([
				// dispatch('getUsers', { roleId: state.activityForm.role ? [ state.activityForm.role ] : [], posId: state.activityForm.pos?.id || null }),
				// dispatch('getPoses'),
				dispatch('getRoles'),
				dispatch('getActivityTypes', {
					roleId: state.activityForm.role,
					start: startFilter,
					end: endFilter,
				}),
			])
				.finally(() => {
					if (name === 'users' && !state.activityForm.id) {
						commit('SET_ACTIVITY_ROLE', {
							roleId: state.activityForm.admins[0]?.role.id || state.activityForm.admins[0]?.role,
							isUsers: true,
						});
					}
				});
		},

		getArea ({ commit }, params: GetAreaParams = {}) {
			return api.get('/area', { params: params })
				.then(resp => {
					const areas = resp.data.payload;
					const paginator = resp.data.paginator;

					if (params.offset) { commit('ADD_AREAS', areas); commit('AREA_PAGINATOR', paginator); }
					else { commit('AREAS_SUCCESS', areas); commit('AREA_PAGINATOR', paginator); }
				})
				.catch(err => { console.log(err); });
		},

		getRegion ({ commit }, params: GetRegionParams = {}) {
			return api.get('/region', { params: params })
				.then(resp => {
					const regions = resp.data.payload;
					const paginator = resp.data.paginator;
					commit('REGIONS_SUCCESS', regions);
					commit('REGIONS_PAGINATION', paginator);
				})
				.catch(err => { console.log(err); });
		},

		getTerritory ({ commit }, params: GetTerritoryParams = {}) {
			return api.get('/territory', { params: params })
				.then(resp => {
					const territory = resp.data.payload;
					const paginator = resp.data.paginator;

					if (params.offset) { commit('ADD_TERRITORIES', territory); commit('TERRITORY_PAGINATOR', paginator); }
					else { commit('TERRITORY_SUCCESS', territory); commit('TERRITORY_PAGINATOR', paginator); }
				})
				.catch(err => { console.log(err); });
		},
	})
	.withActions({
		getFilters ({ dispatch }) {
			dispatch('getArea');
			dispatch('getRegion');
			dispatch('getTerritory');
		},

		getCustomFilters ({ commit }, planningType: 'poses' | 'users') {
			const params: GetFilterParams = {
				key: 'planner',
			};
			if (planningType === 'poses') {
				params.hasPosInfo = true;
			}
			if (planningType === 'users') {
				params.hasAdminInfo = true;
			}

			return api.get('/filter', { params: params })
				.then(resp => {
					const customFilters = resp.data.payload;
					commit('CUSTOM_FILTERS_SUCCESS', customFilters);
					return resp;
				});
		},

		async getPosCategoryFilter ({ state, commit }) {
			if (state.posCategoryFilterLoaded) { return; }

			try {
				// onlyUsed has to stay false, otherwise a freshly configured category disappears from the filters
				const { data } = await api.get('/filter', {
					params: { key: 'plannerPosCategory', hasPosInfo: true, onlyUsed: false },
				});

				const posFilters = data.payload
					.filter((filter): filter is PosCategoryFilter => filter.infoType === 'pos')
					.sort((first, second) => first.id - second.id);

				commit('POS_CATEGORY_FILTER_SUCCESS', posFilters[0] ?? null);
			}
			catch {
				// an unreachable filter must not take the pos list down with it, the dialog falls back to the plain select
			}
		},

		async getPosCategoryCounts ({ state, commit }, params: Omit<GetPosFilterPosInfoParams, 'posInfoId'> = {}) {
			const posInfo = state.posCategoryFilter?.posInfo;

			if (posInfo == undefined) { return; }

			await ignoringAbort(async () => {
				const { data } = await abortableRequest('getPosCategoryCounts', (signal) => api.get('/pos/filter/pos-info', {
					params: { ...params, posInfoId: posInfo.id },
					signal,
				}));

				commit('POS_CATEGORY_COUNTS_SUCCESS', data.payload);
			});
		},

		async showActivity ({ state, dispatch }, eventId: ExtendedEvent['id']) {
			state.activityPos = state.activityEventType = null;
			state.activityAdmins = [];

			const activity = state.activitiesAll.find(event => event.id === eventId);
			if (activity == undefined) { return; }

			if (activity.request?.object != null) {
				await dispatch('getUsers', { adminId: activity.request.object.admins });
			}

			if (activity.groupEvents !== null && activity.groupEvents.length > 0 && activity.groupId !== null) {
				const request = activity.request?.type === 'group'
					? activity.request
					: null;

				const formValues = mapGroupExtendedEventToFormValues(
					{ ...activity, groupEvents: activity.groupEvents, request },
					(id) => state.storedUsers[id] ?? null,
				);

				if (formValues === null) { return; }

				state.activityForm = { ...formValues };
				state.activityFormClone = { ...formValues };

				state.activityForm.role = state.activityFormClone.role = state.activityForm.role ? state.activityForm.role?.id : null;
				return;
			}

			const request = activity.request?.type === 'single'
				? activity.request
				: null;

			const formValues = mapSingleExtendedEventToFormValues(
				{ ...activity, groupEvents: null, request },
				(id) => state.storedUsers[id] ?? null,
			);

			if (formValues === null) { return; }

			state.activityForm = { ...formValues };
			state.activityFormClone = { ...formValues };

			state.activityForm.role = state.activityFormClone.role = state.activityForm.role ? state.activityForm.role?.id : null;
		},
	});


function filterEventsWithGroup (events: Event[]): (Omit<Event, 'groupId'> & { groupId: string })[] {
	return events.filter((event): event is Omit<Event, 'groupId'> & { groupId: string } =>
		event.groupId !== null);
}

function buildAdminGroups (groupedByGroupIdDay: Dict<Event[]>): ExtendedEvent[] {
	return Object.entries(groupedByGroupIdDay).map(([ _key, groupEvents ]) => {
		const sortedEvents = [ ...groupEvents ].sort((a, b) =>
			moment(a.start).diff(moment(b.start)));

		const first = sortedEvents[0];
		const last = sortedEvents[sortedEvents.length - 1];

		return {
			...first,
			end: last.end,
			groupEvents: sortedEvents,
		};
	});
}

function buildPosGroups (eventsWithGroup: (Omit<Event, 'groupId'> & { groupId: string })[], groupedByGroupIdDay: Dict<Event[]>): ExtendedEvent[] {
	return eventsWithGroup.map((event) => {
		const day = moment(event.start).format('YYYY-MM-DD');
		const groupEvents = groupedByGroupIdDay[`${ event.groupId }-${ day }`];

		if (groupEvents === undefined) {
			return {
				...event,
				groupEvents: null,
			};
		}

		const sortedEvents = [ ...groupEvents ].sort((a, b) =>
			moment(a.start).diff(moment(b.start)));

		return {
			...event,
			groupEvents: sortedEvents,
		};
	});
}

// NOTE: extract form values from state management instead of re-typing return value
function mapGroupExtendedEventToFormValues (
	activity: ExtendedEvent & { groupEvents: Event[]; request: GroupEventsRequest | null },
	adminById: (id: Admin['id']) => Admin | null,
): object | null {
	if (activity.request?.object != null) {
		const { start, end, pos, admins } = activity.request.object;

		const matchedAdmins = admins
			.map(adminById)
			.filter((admin): admin is Admin => admin !== null);

		return {
			...activity,
			supervisors: activity.supervisors.map(({ id }) => id),
			admins: matchedAdmins,
			start,
			end,
			mode: 'group',
			posIds: pos,
			freezeNote: activity.request.request.note,
			request: activity.request,
		};
	}

	const posIds = Array.from(new Set(activity.groupEvents
		.map(({ pos }) => pos)
		.filter((p): p is PosLight => p !== null)
		.map(p => p.id) ?? []));

	const start = activity.groupEvents?.[0] !== undefined ? activity.groupEvents[0].start : activity.start;
	const end = activity.groupEvents?.[0] !== undefined ? activity.groupEvents[activity.groupEvents.length - 1].end : activity.end;

	return {
		...activity,
		supervisors: activity.supervisors.map(({ id }) => id),
		mode: 'group',
		start,
		end,
		posIds,
		freezeNote: null,
	};
}

// NOTE: extract form values from state management instead of re-typing return value
function mapSingleExtendedEventToFormValues (
	activity: ExtendedEvent & { groupEvents: null; request: SingleEventRequest | null },
	adminById: (id: Admin['id']) => Admin | null,
): object | null {
	if (activity.request?.object != null) {
		const { start, end, pos, admins } = activity.request.object;

		const matchedAdmins = admins
			.map(adminById)
			.filter((admin): admin is Admin => admin !== null);

		return {
			...activity,
			supervisors: activity.supervisors.map(({ id }) => id),
			admins: matchedAdmins,
			start,
			end,
			mode: 'single',
			posIds: pos !== null ? [ pos ] : [],
			freezeNote: activity.request.request.note,
			request: activity.request,
		};
	}

	const posIds = activity.pos?.id !== null ? [ activity.pos?.id ] : [];

	return {
		...activity,
		posIds,
		freezeNote: null,
		supervisors: activity.supervisors.map(({ id }) => id),
		mode: 'single',
	};
}
